import { SagaGenerator, select } from 'typed-redux-saga';

import { getUserSelector, toPassport, toSsoPassport } from '..';
import { createPassportYandexSsoAuthUrl } from '../../../../../../common/helpers/sso-passport-url';
import { YandexUid } from '../../../../types';
import { getPath } from '../../../helpers/get-path';
import { ACTION, FEATURE } from '../../../helpers/logger-enums';
import { createLogger } from '../../../helpers/loggerFacade';
import { getConfigSelector } from '../../config';
import { selectCookierHost } from '../../config/selectors';
import { selectIsEasyAuthEnabled } from '../../flags/selectors';
import { FROM_PASSPORT } from '../enums';

const logger = createLogger('toPassport');

const addParams = (base: string, params?: Record<string, string>): string => {
    const result = new URL(base);
    for (const param in params) {
        result.searchParams.set(param, params[param]);
    }
    return result.toString();
};

function* wrapRetpathInCookierRedirect(retpath: string): SagaGenerator<string> {
    const isEasyAuthEnabled = yield* select(selectIsEasyAuthEnabled);
    const cookierHost = yield* select(selectCookierHost);

    if (isEasyAuthEnabled) {
        return addParams(`${cookierHost}/yandex360session`, { retpath });
    }
    return retpath;
}

function createPassportChangeUserUrl({
    passportBase,
    uid,
    yu,
    retPath,
}: {
    passportBase: string;
    uid: string; // uid пользователя на который меняем
    yu: YandexUid; // значение куки yandexuid текущего пользователя
    retPath?: string;
}): string {
    const url = new URL('/passport', passportBase);

    url.searchParams.set('mode', 'embeddedauth');
    url.searchParams.set('action', 'change_default');
    url.searchParams.set('uid', uid);
    url.searchParams.set('yu', yu);

    if (retPath) {
        url.searchParams.set('retpath', retPath);
    }

    return url.toString();
}

export const toPassportSaga = function* ({ payload }: ReturnType<typeof toPassport>): SagaGenerator<void> {
    const config = yield* select(getConfigSelector);
    if (!config.yaServices) return;

    const initialRetpath = payload?.retpath ?? window.location.pathname;
    const backpath = payload?.backpath;
    const baseUrl = payload?.url || '/auth';

    const retpathWithOrigin = `${window.location.origin}${getPath(initialRetpath)}`;
    const finalRetpath = yield* wrapRetpathInCookierRedirect(retpathWithOrigin);

    const url = new URL(baseUrl, config.yaServices.passport);
    url.searchParams.set('retpath', addParams(finalRetpath, { [FROM_PASSPORT]: '1' }));
    if (backpath) {
        url.searchParams.set('backpath', addParams(`${window.location.origin}${getPath(backpath)}`));
    }
    url.searchParams.set('origin', 'b2b360');

    logger.featureEvent({ feature: FEATURE.AUTHORIZATION, action: ACTION.LOGIN });
    window.location.href = url.toString();
};

export function* toSsoPassportSaga({ payload }: ReturnType<typeof toSsoPassport>) {
    const config = yield* select(getConfigSelector);
    const user = yield* select(getUserSelector);
    const conferenceUri = window.location.href;

    if (!config.yaServices || !conferenceUri) {
        return;
    }

    const finalConferenceUri = yield* wrapRetpathInCookierRedirect(conferenceUri);

    const retPath = (() => {
        if (user && user.yandexuid && user.uid) {
            return createPassportChangeUserUrl({
                passportBase: config.yaServices.passport,
                uid: user.uid,
                yu: user.yandexuid,
                retPath: finalConferenceUri,
            });
        }

        return finalConferenceUri;
    })();
    window.location.href = createPassportYandexSsoAuthUrl({
        passportBase: config.yaServices.passport,
        login: payload.login,
        /**
         * Автосабмит (нет экрана подтверждения в Паспорте) работает только для origin
         * telemost_web/telemost_touch. В клиентском SSO-флоу оставляем web-вариант.
         */
        origin: 'telemost_web',
        retPath,
    });
}
